export const DEFAULT_VHS_OPTIONS = {
  bandwidth: 16000000,
  useBandwidthFromLocalStorage: true,
  useNetworkInformationApi: false,
  limitRenditionByPlayerDimensions: false,
};

export const DEFAULT_QUALITY_HEIGHT = 720;

const shortSide = (resolution) =>
  resolution?.width && resolution?.height
    ? Math.min(resolution.width, resolution.height)
    : null;

const isPlayable = (playlist) =>
  !playlist.disabled &&
  !(playlist.excludeUntil && playlist.excludeUntil > Date.now());

export const renditionHeight = (playlist) =>
  shortSide(playlist?.attributes?.RESOLUTION);

// The tallest rendition up to `height`, or the shortest one when the ladder
// starts above it. Excluded and viewer-disabled renditions are not candidates.
export const targetRendition = (playlists, height) => {
  const ladder = (playlists || [])
    .filter((playlist) => isPlayable(playlist) && renditionHeight(playlist))
    .sort((current, next) => renditionHeight(current) - renditionHeight(next));

  const upToHeight = ladder.filter(
    (playlist) => renditionHeight(playlist) <= height,
  );

  return upToHeight[upToHeight.length - 1] || ladder[0] || null;
};

/**
 * Fix playback to one rendition, so mismatched keyframe intervals between
 * renditions never surface as a jump.
 *
 * Renditions are chosen here, at the source of the decision, rather than
 * corrected afterwards through quality levels: those exist only once VHS has
 * already fetched a chunklist, which is one rendition switch too late.
 *
 * Applies to recordings only. Nothing in a main manifest says whether it is
 * live, so a live stream is recognised at 'loadedmetadata' and handed back.
 */
export const pinDefaultQuality = (
  player,
  qualitySelector,
  height = DEFAULT_QUALITY_HEIGHT,
) => {
  const vhs = player.tech(true)?.vhs;
  if (!vhs || !qualitySelector) return false;

  // Cleared by the viewer's own choice, which outranks ours -- including Auto,
  // which asks for adaptation back.
  let pinned = height;

  const adaptive = vhs.selectPlaylist;
  vhs.selectPlaylist = function (...args) {
    const rendition =
      pinned && targetRendition(vhs.playlists?.main?.playlists, pinned);

    return rendition || adaptive.call(this, ...args);
  };

  const { setQuality } = qualitySelector;
  qualitySelector.setQuality = (quality) => {
    pinned = null;
    setQuality.call(qualitySelector, quality);
  };

  player.one('loadedmetadata', () => {
    // A live stream has no fixed duration, and adaptation matters more there
    // than a steady rendition: a stall cannot be buffered around.
    if (!Number.isFinite(player.duration())) {
      pinned = null;
      return;
    }

    // The menu is built from the renditions VHS reports at 'loadedmetadata',
    // and it is built reading Auto. Name the rendition that is playing.
    const playing = renditionHeight(vhs.playlists?.media());
    if (!pinned || !playing) return;

    setQuality.call(qualitySelector, playing);

    // The plugin only moves the tick for a click of its own.
    const items = qualitySelector._qualityButton?.items || [];
    items.forEach((item) => item.selected(item.item?.value === playing));
  });

  return true;
};
